/**
 * 时间螺线的「路线」：从第一张照片那天起每天一个锚点，相邻两天之间按弧长线性插值，记下累计弧长。
 * 星轨（trail.ts）沿它铺星尘、彗星沿它走；时光漫游（roam.ts）的镜头也沿它走——同一套计算，
 * 画出来的星轨和镜头走的路一定是一条，不会各走各的。
 */
import { SPIRAL, addDays, dayAnchor, daysBetween, type LocalDate, type Vec3 } from '@star-sea/shared';

/** 一条铺好的路线：锚点、每天的累计弧长、实线段（first → last）和虚线尾巴（→ end） */
export interface TrailPath {
  /** 第一张照片那天（弧长 0） */
  first: LocalDate;
  /** 锚点数 − 1（至少 1，first === end 时也还有第二天那个锚点） */
  days: number;
  /** (days + 1) 个锚点，每个 3 个数 */
  anchors: Float32Array;
  /** 每天处的累计弧长 */
  arc: Float32Array;
  /** 最新一张照片那天的弧长：实线段到这为止，之后是渐隐的虚线 */
  sLast: number;
  /** 到 end（「未完待续」那头）的总弧长 */
  total: number;
}

/**
 * 从 first（第一张照片的日期）铺到 end；last 是最新一张照片的日期。
 * 和星轨 trail.ts 的铺法完全一样，只是抽出来共用。
 */
export function trailPath(first: LocalDate, last: LocalDate, end: LocalDate, since: LocalDate): TrailPath {
  const days = Math.max(1, daysBetween(first, end));
  const anchors = new Float32Array((days + 1) * 3), arc = new Float32Array(days + 1), a: Vec3 = { x: 0, y: 0, z: 0 };
  for (let k = 0; k <= days; k++) {
    dayAnchor(addDays(first, k), since, SPIRAL, a);
    anchors.set([a.x, a.y, a.z], k * 3);
    if (k > 0) arc[k] = arc[k - 1]! + Math.hypot(a.x - anchors[k * 3 - 3]!, a.y - anchors[k * 3 - 2]!, a.z - anchors[k * 3 - 1]!);
  }
  const lastK = Math.min(days, Math.max(0, daysBetween(first, last)));
  return { first, days, anchors, arc, sLast: arc[lastK]!, total: arc[days]! };
}

/** 这一天的弧长；早于 first、晚于 end 的夹到两头 */
export function arcOf(path: TrailPath, date: LocalDate): number {
  return path.arc[Math.min(path.days, Math.max(0, daysBetween(path.first, date)))]!;
}

/** 弧长 s 处的位置：相邻两天的锚点之间线性插值（二分找段）；s 夹到 [0, total] */
export function pointAt(path: TrailPath, s: number, out: Vec3): Vec3 {
  const { anchors, arc, days, total } = path;
  s = Math.min(Math.max(s, 0), total);
  // 二分：找满足 arc[k] <= s <= arc[k + 1] 的段 k
  let lo = 0, hi = days - 1;
  while (lo < hi) {
    const mid = (lo + hi) >> 1;
    if (arc[mid + 1]! < s) lo = mid + 1;
    else hi = mid;
  }
  const f = Math.min(1, Math.max(0, (s - arc[lo]!) / Math.max(1e-6, arc[lo + 1]! - arc[lo]!)));
  out.x = anchors[lo * 3]! + (anchors[lo * 3 + 3]! - anchors[lo * 3]!) * f;
  out.y = anchors[lo * 3 + 1]! + (anchors[lo * 3 + 4]! - anchors[lo * 3 + 1]!) * f;
  out.z = anchors[lo * 3 + 2]! + (anchors[lo * 3 + 5]! - anchors[lo * 3 + 2]!) * f;
  return out;
}
